<script setup>
// Right Chat - criar / renomear / excluir board.
import { ref, computed } from 'vue';
import RightKanbanModal from './RightKanbanModal.vue';
import { useRightKanban } from '../../../composables/useRightKanban';

const props = defineProps({
  board: { type: Object, default: null },
});

const emit = defineEmits(['close']);

const { createBoard, renameBoard, deleteBoard, saving, error } = useRightKanban();

const isEdit = computed(() => !!props.board);
const name = ref(props.board?.name || '');
const confirmingDelete = ref(false);

const canSubmit = computed(() => name.value.trim().length > 0 && !saving.value);

const submit = async () => {
  if (!canSubmit.value) return;
  const result = isEdit.value
    ? await renameBoard(props.board.id, name.value.trim())
    : await createBoard(name.value.trim());
  if (result) emit('close');
};

const remove = async () => {
  // Board apagado leva junto etapas e cards (dependent: :destroy). Confirmacao inline em
  // vez de window.confirm: dialogo nativo trava a aba inteira quando a tela e pilotada.
  if (!confirmingDelete.value) {
    confirmingDelete.value = true;
    return;
  }
  const result = await deleteBoard(props.board.id);
  if (result) emit('close');
};
</script>

<template>
  <RightKanbanModal
    :title="isEdit ? 'Editar board' : 'Novo board'"
    @close="emit('close')"
  >
    <label class="rk-field">
      <span class="rk-field__label">Nome</span>
      <input
        v-model="name"
        class="rk-field__input"
        type="text"
        maxlength="255"
        placeholder="Funil de vendas"
        @keyup.enter="submit"
      />
    </label>

    <p v-if="isEdit && confirmingDelete" class="rk-danger">
      Excluir o board apaga tambem as etapas e os cards dele. Clique em "Excluir" de
      novo para confirmar.
    </p>

    <p v-if="error" class="rk-error">{{ error }}</p>

    <template #footer>
      <button
        v-if="isEdit"
        type="button"
        class="rk-btn rk-btn--danger"
        :disabled="saving"
        @click="remove"
      >
        Excluir
      </button>
      <button type="button" class="rk-btn" @click="emit('close')">Cancelar</button>
      <button
        type="button"
        class="rk-btn rk-btn--primary"
        :disabled="!canSubmit"
        @click="submit"
      >
        {{ saving ? 'Salvando...' : 'Salvar' }}
      </button>
    </template>
  </RightKanbanModal>
</template>

<style scoped>
.rk-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.rk-field__label {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}

.rk-field__input {
  padding: 8px 10px;
  font-size: 13px;
  color: #0f172a;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
}

.rk-field__input:focus {
  border-color: #4f46e5;
  outline: none;
}

.rk-error,
.rk-danger {
  padding: 8px 10px;
  margin: 0;
  font-size: 12px;
  border-radius: 8px;
}

.rk-error {
  color: #b91c1c;
  background: #fee2e2;
}

.rk-danger {
  color: #92400e;
  background: #fef3c7;
}

.rk-btn {
  padding: 7px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
}

.rk-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.rk-btn--primary {
  color: #ffffff;
  background: #4f46e5;
  border-color: #4f46e5;
}

.rk-btn--danger {
  margin-right: auto;
  color: #b91c1c;
  border-color: #fecaca;
}

.dark .rk-field__label {
  color: #cbd5e1;
}

.dark .rk-field__input {
  color: #e2e8f0;
  background: #0f172a;
  border-color: #334155;
}

.dark .rk-btn {
  color: #cbd5e1;
  background: #1e293b;
  border-color: #334155;
}

.dark .rk-btn--primary {
  color: #ffffff;
  background: #4f46e5;
  border-color: #4f46e5;
}
</style>
